import type { Permission } from "@shared/constants/permissions";
import type {
  MagicLinkRedeemPayload,
  MagicLinkVerifyCodePayload,
  MagicLinkRedeemResponse,
  MagicLinkPeekPayload,
  MagicLinkPeekResponse,
} from "@shared/types/magicLink";
import type { OrgType } from "@shared/types/org";
import type { Role } from "@shared/types/role";

import apiClient from "./axios";

// Re-export magic link types for consumers
export type {
  MagicLinkRedeemPayload,
  MagicLinkVerifyCodePayload,
  MagicLinkRedeemResponse,
  MagicLinkPeekPayload,
  MagicLinkPeekResponse,
};

export interface RequestCodePayload {
  email: string;
}

export interface VerifyCodePayload {
  code: string;
  email: string;
}

export interface RefreshTokenPayload {
  accessToken?: string; // Optional: current access token to preserve impersonation data
  refreshToken: string;
}

export interface LogoutPayload {
  accessToken?: string;
  allDevices?: boolean;
  refreshToken: string;
}

export interface AuthResponse {
  accessToken: string;
  expiresIn: number;
  refreshToken: string;
  user: {
    email: string;
    firstName?: string;
    id: string;
    lastName?: string;
    name: string;
    organization?: {
      aiEnabled: boolean;
      aiFalloutInsightsEnabled?: boolean;
      allowedCustomSyncers?: string[];
      caseHealthEnabled?: boolean;
      dataSyncEnabled: boolean;
      dualRepEnabled: boolean;
      id: string;
      name: string;
      orgType: OrgType;
      variableSyncFrequencyEnabled?: boolean;
    };
    permissions: Permission[];
    roles?: Role[];
    termsAcceptedAt?: null | string;
  };
}

export interface RefreshResponse {
  accessToken: string;
  expiresIn: number;
}

export interface UserData {
  /**
   * Orgs whose data this user can see — root identity + its canonicals
   * + aliases. Backend-derived via getAccessibleOrgIds and returned
   * from /auth/me. Empty for null-org users (their scope comes from
   * viewAssignments). Optional because the slim AuthResponse.user
   * returned at login time doesn't include it; fetchUserData() then
   * populates the full UserData from /auth/me.
   */
  accessibleOrgIds?: string[];
  avatarUrl?: null | string;
  email: string;
  firstName?: string;
  /** Whether to offer API Access: a share with API access switched on (RAP-773). */
  hasApiAccess?: boolean;
  /** Whether the user holds an unrevoked API key, so they can still revoke it without access. */
  hasApiKey?: boolean;
  id: string;
  lastName?: string;
  name: string;
  organization?: {
    aiEnabled: boolean;
    aiFalloutInsightsEnabled?: boolean;
    allowedCustomSyncers?: string[];
    caseHealthEnabled?: boolean;
    dataSyncEnabled: boolean;
    dualRepEnabled: boolean;
    id: string;
    isDemo?: boolean;
    name: string;
    orgType: OrgType;
    // Superadmin-granted: the org may set per-source sync intervals (RAP-613).
    variableSyncFrequencyEnabled?: boolean;
  };
  organizationId?: string;
  permissions: Permission[];
  /**
   * Which application this user is served (RAP-630). Resolved server-side at the firm's
   * identity anchor, which is why it sits beside `organization` rather than inside it — the
   * answer is a property of the firm, not of whichever org row the user hangs off.
   *
   * Optional because the slim login response does not carry it; `fetchUserData()` fills it in
   * from /auth/me before any guard runs. Absent therefore means "not known yet", and every
   * reader must treat that as OFF — see `isReferringPortalEnabled` in the auth store.
   */
  referringPortalEnabled?: boolean;
  roles?: Role[];
  rootIdentity?: {
    id: string;
    logoUrl: null | string;
    name: string;
  } | null;
  termsAcceptedAt?: null | string;
  userReportCadence?: string; // User's global report cadence override
  viewAssignments?: Array<{
    adminGroupByField: null | string;
    adminReportCadence: string;
    adminReportsEnabled: boolean;
    campaignId: string;
    campaignName: string;
    handlingFirmName?: string;
    id: string;
    /** Scope-org of this assignment — the firm that shared the view with the user. */
    organizationId?: string;
    organizationName?: string;
    userGroupByField: null | string;
    userReportsEnabled: boolean | null;
    viewId: null | string;
    viewName: null | string;
  }>;
}

const authApi = {
  /**
   * Request a verification code to be sent to the user's email
   */
  requestCode: (payload: RequestCodePayload) => {
    return apiClient.post("/auth/request-code", payload);
  },

  /**
   * Verify the code and login
   */
  login: (payload: VerifyCodePayload) => {
    return apiClient.post<AuthResponse>("/auth/login", payload);
  },

  /**
   * Refresh the access token using the refresh token
   */
  refreshToken: (payload: RefreshTokenPayload) => {
    return apiClient.post<RefreshResponse>("/auth/refresh", payload);
  },

  /**
   * Logout the user, optionally from all devices
   */
  logout: (payload: LogoutPayload) => {
    return apiClient.post("/auth/logout", payload);
  },

  /**
   * Get the current user's information
   */
  getUser: () => {
    return apiClient.get<UserData>("/auth/me");
  },

  /**
   * Accept the Terms of Use for the current user
   */
  acceptTerms: () => {
    return apiClient.post<{ termsAcceptedAt: string }>("/users/me/accept-terms");
  },

  /**
   * Disable reporting for a user via unsubscribe token (public, no auth)
   */
  disableReporting: (unsubscribeToken: string) => {
    return apiClient.post<{ message: string }>("/auth/magic/disable-reporting", {
      unsubscribeToken,
    });
  },

  /**
   * Peek at a magic link without redeeming it
   * Returns link status and recipient info (masked) for switch-account prompts
   */
  peekMagicLink: (payload: MagicLinkPeekPayload) => {
    return apiClient.post<MagicLinkPeekResponse>("/auth/magic/peek", payload);
  },

  /**
   * Redeem a magic link token
   * Returns success with auth tokens, or expired with email info for verification
   */
  redeemMagicLink: (payload: MagicLinkRedeemPayload) => {
    return apiClient.post<MagicLinkRedeemResponse>("/auth/magic/redeem", payload);
  },

  /**
   * Verify code for an expired magic link
   * Creates a session after successful verification
   */
  verifyMagicLinkCode: (payload: MagicLinkVerifyCodePayload) => {
    return apiClient.post<MagicLinkRedeemResponse>("/auth/magic/verify-code", payload);
  },
};

export default authApi;
